import { DotMatrix } from '@clickguard/design-system'
import { useAuth0 } from '@auth0/auth0-react'
import { ProductAccountDTO, AssetDTO } from 'api-models'
import { useStores } from 'hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Onboarding, ProductAccounts } from 'services/user'
import { Assets } from 'services/user/assets'
import { parseError } from 'utils/errors'
import { setItemsToLocalStorage } from '../../../utils'

import { SetupSidebar } from './components/setup-sidebar'
import {
	ProtectWebsiteComponent,
	ConnectAdAccountComponent,
	InstallTrackingCode
} from 'components/setup-v2'
import {
	NeedHelpTag,
	NeedHelpPanel
} from 'components/setup-v2/need-help-button'
import { useAccountStore } from 'store/account-store'
import { hasFeature } from 'components/setup-v2/install-tracking-code/install-tracking-code'

export const SetupV2 = () => {
	const [account, setAccount] = useState<ProductAccountDTO | null>(null)
	const [asset, setAsset] = useState<AssetDTO | null>(null)
	const [loading, setLoading] = useState(false)
	const [showInterstitial, setShowInterstitial] = useState<boolean | null>(null)
	const [adAccountConnected, setAdAccountConnected] = useState(false)
	const [helpPanelOpen, setHelpPanelOpen] = useState(false)
	const [isManualTracking, setIsManualTracking] = useState(false)
	const navigate = useNavigate()
	const location = useLocation()
	const { isAuthenticated, loginWithRedirect } = useAuth0()
	const { accountId, assetId } = useParams()
	const { assetStore, userStore } = useStores()
	const step = location.pathname.includes('/tracking')
		? '3'
		: assetId
		? '2'
		: '1'
	const { allAssets, setAsset: setAssetInStore, setAssetForId } = assetStore
	const { user } = userStore
	const storeAccount = useAccountStore((state) => state.account)

	// Sync local account state when store account updates (e.g. after interstitial sets onboardingPath)
	useEffect(() => {
		if (storeAccount && account && storeAccount.id === account.id) {
			setAccount(storeAccount)
		}
	}, [storeAccount])

	const onboardingPath =
		(storeAccount?.setup as any)?.onboardingPath ||
		(account?.setup as any)?.onboardingPath
	const showNeedHelp =
		(step === '3' || (step === '2' && isManualTracking)) &&
		onboardingPath === 'self-onboarding'

	if (!isAuthenticated) {
		loginWithRedirect({
			appState: {
				returnTo: window.location.pathname + window.location.search
			}
		})
		return <></>
	}
	const hasServerSideTracking = account
		? hasFeature(account, 'server_side_tracking')
		: false

	const init = async (accountId: string) => {
		setLoading(true)
		try {
			const accountData = await ProductAccounts.getById(accountId)
			setItemsToLocalStorage([
				{ key: 'selected_account', value: accountData.id }
			])
			setAccount(accountData)

			Onboarding.checkInterstitialEligibility(accountId)
				.then((result) => {
					setShowInterstitial(result.showInterstitial)
				})
				.catch(() => {
					// On error, default to not showing interstitial
					setShowInterstitial(false)
				})

			// If assetId is provided, load the asset
			if (assetId) {
				try {
					const assetData = await Assets.getById(accountId, assetId)
					setAsset(assetData)
					setAssetInStore(assetData)
					setItemsToLocalStorage([
						{ key: 'selected_asset', value: assetData.id },
						{ key: 'selected_account', value: accountData.id }
					])
				} catch (err) {
					console.error('Failed to load asset:', parseError(err))
					navigate(`/setup/${accountId}`)
				}
			} else {
				// For step 1, try to find existing asset
				const foundAsset = allAssets.find((a) => a.account_id === accountId)
				if (foundAsset) {
					setAsset(foundAsset)
					setAssetInStore(foundAsset)
					setItemsToLocalStorage([
						{ key: 'selected_asset', value: foundAsset.id },
						{ key: 'selected_account', value: accountData.id }
					])
					// Mid-onboarding (website saved, no ad account yet) step 1 is
					// done: going back must not ask for the website again. After
					// onboarding this URL is "Protect a new website" and stays.
					if (accountData.setup?.step === 'WEBSITE_PROTECTED') {
						navigate(`/setup/${accountId}/${foundAsset.id}`, { replace: true })
					}
				}
			}

			setLoading(false)
		} catch (err) {
			setLoading(false)
			console.error('Failed to load account:', parseError(err))
		}
	}

	const handleAccountUpdate = (updatedAccount: ProductAccountDTO) => {
		useAccountStore.getState().setAccount(updatedAccount)
		setAccount(updatedAccount)
	}

	const handleDone = (newAssetId: string) => {
		// Navigate to next step (connect ad account)
		if (accountId) {
			navigate(`/setup/${accountId}/${newAssetId}`, { replace: true })
		}
	}

	const handleAdAccountConnected = async () => {
		// Navigate to step 3 (tracking code installation)
		if (accountId && assetId) {
			try {
				const resultInterstitial =
					await Onboarding.checkInterstitialEligibility(accountId)
				if (resultInterstitial) {
					setShowInterstitial(resultInterstitial.showInterstitial)
				}
			} catch (err) {
				console.error(
					'Failed to check interstitial eligibility:',
					parseError(err)
				)
				setShowInterstitial(false)
			}
			navigate(`/setup/${accountId}/${assetId}/tracking`)
		}
	}

	const handleTrackingCodeInstalled = async () => {
		// Navigate to workspace after tracking code installation
		if (accountId && assetId) {
			await Onboarding.updateSetup(accountId, {
				step: 'SETUP_COMPLETE',
				tracking_code_opted_out: false
			})
			await setAssetForId(accountId, assetId)
			navigate(`/workspace/${accountId}/asset/${assetId}`)
			window.location.reload()
		}
	}

	useEffect(() => {
		if (accountId) {
			init(accountId)
		}
	}, [accountId, assetId])

	return (
		<div className="bg-t-smoke bg-gradient-to-br md:p-8 p-4 min-h-screen flex flex-col from-[var(--setup-grad-from,#C7C5F9)] via-[var(--setup-grad-via,#CFE7FF)] to-[var(--setup-grad-to,#F3E6FF)]">
			<div className="flex flex-1 items-center justify-center">
				<div className="relative w-fit max-w-full h-full items-center justify-center lg:flex lg:gap-10 xl:block">
					<div className="xl:absolute right-full xl:mr-[80px] z-10 flex items-center justify-center xl:top-1/2 xl:-translate-y-1/2">
						<SetupSidebar
							step={step}
							adAccountConnected={adAccountConnected}
							accountId={accountId}
							assetId={assetId}
							knowledgeBaseUrl={
								step === '3'
									? hasServerSideTracking
										? 'https://docs.clickguard.com/en/articles/21547-serverside-tracking-setup-process'
										: 'https://docs.clickguard.com/en/articles/21430-how-to-install-the-clickguard-tracking-code'
									: undefined
							}
						/>
					</div>
					{account &&
						user &&
						!loading &&
						(step === '2' || step === '3') &&
						asset &&
						showNeedHelp &&
						helpPanelOpen && (
							<div className="absolute left-full ml-[80px] top-0 z-10 w-max">
								<NeedHelpPanel
									accountId={accountId}
									onUpdateAccount={handleAccountUpdate}
									onClose={() => setHelpPanelOpen(false)}
								/>
							</div>
						)}
					<div
						className={`flex flex-row items-center overflow-visible ${
							showNeedHelp ? 'items-start' : 'items-center'
						}`}
					>
						{/* Right Panel - Form */}
						{account && user && !loading ? (
							step === '1' ? (
								<ProtectWebsiteComponent
									account={account}
									user={user}
									onSuccess={handleDone}
								/>
							) : step === '2' && asset ? (
								<div className="relative md:w-[620px] w-full">
									{showNeedHelp && (
										<div className="absolute right-0 -top-[36px] z-10">
											<NeedHelpTag
												isOpen={helpPanelOpen}
												onToggle={() => setHelpPanelOpen(!helpPanelOpen)}
											/>
										</div>
									)}
									<ConnectAdAccountComponent
										account={account}
										user={user}
										asset={asset}
										onSuccess={handleAdAccountConnected}
										showInterstitial={showInterstitial ?? false}
										onAdAccountConnected={() => setAdAccountConnected(true)}
										onManualTrackingChange={setIsManualTracking}
										// Qualification is for the trial: a website added to a
										// workspace whose setup is complete is not screened again.
										screenSpend={account.setup?.step !== 'SETUP_COMPLETE'}
									/>
								</div>
							) : step === '3' && asset ? (
								<div className="relative md:w-[620px] w-full">
									{showNeedHelp && (
										<div className="absolute right-0 -top-[36px] z-10">
											<NeedHelpTag
												isOpen={helpPanelOpen}
												onToggle={() => setHelpPanelOpen(!helpPanelOpen)}
											/>
										</div>
									)}
									<InstallTrackingCode
										account={account}
										asset={asset}
										onSuccess={handleTrackingCodeInstalled}
										showInterstitial={showInterstitial ?? false}
										helpPanelOpen={showNeedHelp && helpPanelOpen}
									/>
								</div>
							) : (
								<div className="flex md:w-[620px] w-full items-center justify-center">
									<div className="w-full h-96 flex flex-col justify-center items-center text-center space-y-4">
										<DotMatrix size="lg" />
									</div>
								</div>
							)
						) : (
							<div className="flex md:w-[620px] w-full items-center justify-center">
								<div className="w-full h-96 flex flex-col justify-center items-center text-center space-y-4">
									<DotMatrix size="lg" />
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
